import {
	ADMIN_STEPS_MULTIPLE,
	ADMIN_STEPS_MULTIPLE_EXCLUDED,
	ADMIN_STEPS_SIMPLE,
	ADMIN_STEPS_SIMPLE_EXCLUDED,
	ADMIN_SUB_STATUS_MULTIPLE,
	ADMIN_SUB_STATUS_MULTIPLE_EXCLUDED,
	ADMIN_SUB_STATUS_SIMPLE,
	ADMIN_SUB_STATUS_SIMPLE_EXCLUDED,
} from '../../../OrderDetailsStepper/stepData';
import { OrderInterface } from '../../../OrdersTable/interfaces/Order.interface';
import {
	AdminOrderStatusesEnum,
	OrderStatesEnum,
	OrderStatusesEnum,
} from '../../../../services/orders.services';
import { resolveExecutionStatus } from '../../../../utils/order-execution.util';

export const getAdminStepSets = (order: OrderInterface) => {
	const simple = (order?.trucks?.length ?? 0) <= 1;
	const deliveryIncluded = !!order?.deliveryIncluded;

	if (simple) {
		return deliveryIncluded
			? { steps: ADMIN_STEPS_SIMPLE, subs: ADMIN_SUB_STATUS_SIMPLE }
			: {
					steps: ADMIN_STEPS_SIMPLE_EXCLUDED,
					subs: ADMIN_SUB_STATUS_SIMPLE_EXCLUDED,
			  };
	} else {
		return deliveryIncluded
			? { steps: ADMIN_STEPS_MULTIPLE, subs: ADMIN_SUB_STATUS_MULTIPLE }
			: {
					steps: ADMIN_STEPS_MULTIPLE_EXCLUDED,
					subs: ADMIN_SUB_STATUS_MULTIPLE_EXCLUDED,
			  };
	}
};

const getAdminProgressSimpleIncluded = (
	order: OrderInterface,
): { current: number; isFinalStep: boolean } => {
	const status = order.status as
		| OrderStatusesEnum
		| AdminOrderStatusesEnum
		| undefined;

	if (status === OrderStatusesEnum.PAID) {
		return { current: ADMIN_STEPS_SIMPLE.length, isFinalStep: true };
	}

	switch (resolveExecutionStatus(order)) {
		case OrderStatesEnum.TO_DO:
			return { current: 1, isFinalStep: false };
		case OrderStatesEnum.AWAITING_CONFIRMATION:
			return { current: 2, isFinalStep: false };
		case OrderStatesEnum.TRANSPORTER_CONFIRMED:
			return {
				current: order?.trucks?.[0]?.driver ? 4 : 3,
				isFinalStep: false,
			};
		case OrderStatesEnum.TRUCK_CONFIRMED:
		case OrderStatusesEnum.REQUESTED:
		case OrderStatusesEnum.ACCEPTED:
			return { current: 4, isFinalStep: false };
		case OrderStatesEnum.TO_LOADING:
			return { current: 5, isFinalStep: false };
		case OrderStatesEnum.ARRIVED_TO_LOADING:
		case OrderStatesEnum.LOADING:
			return { current: 6, isFinalStep: false };
		case OrderStatesEnum.LOADED:
		case OrderStatesEnum.TO_UNLOADING:
			return { current: 7, isFinalStep: false };
		case OrderStatesEnum.ARRIVED_TO_UNLOADING:
			return { current: 8, isFinalStep: false };
		case OrderStatesEnum.UNLOADING:
			return { current: 9, isFinalStep: false };
		case OrderStatesEnum.UNLOADED:
			return { current: 10, isFinalStep: false };
		case OrderStatesEnum.COMPLETED:
			return { current: 11, isFinalStep: false };
		default:
			return { current: 1, isFinalStep: false };
	}
};

const getAdminProgressMultipleIncluded = (
	order: OrderInterface,
): { current: number; isFinalStep: boolean } => {
	const state = order.state as OrderStatesEnum | undefined;
	const status = order.status as
		| AdminOrderStatusesEnum
		| OrderStatusesEnum
		| undefined;

	const totalSteps = ADMIN_STEPS_MULTIPLE.length;
	const penultimateStep = Math.max(1, totalSteps - 1);

	const isStateInRange =
		state != null &&
		[
			OrderStatesEnum.TRUCK_CONFIRMED,
			OrderStatesEnum.TO_LOADING,
			OrderStatesEnum.ARRIVED_TO_LOADING,
			OrderStatesEnum.LOADING,
			OrderStatesEnum.LOADED,
			OrderStatesEnum.TO_UNLOADING,
			OrderStatesEnum.ARRIVED_TO_UNLOADING,
			OrderStatesEnum.UNLOADING,
			OrderStatesEnum.UNLOADED,
			OrderStatesEnum.COMPLETED,
		].includes(state);

	if (state === OrderStatesEnum.COMPLETED) {
		if (status === AdminOrderStatusesEnum.DELIVERED) {
			return { current: penultimateStep, isFinalStep: false };
		}
		if (status === OrderStatusesEnum.PAID) {
			return { current: totalSteps, isFinalStep: true };
		}
	}

	if (
		isStateInRange &&
		status === AdminOrderStatusesEnum.AWAITING_CONFIRMATION
	) {
		return { current: 3, isFinalStep: false };
	}

	switch (status) {
		case AdminOrderStatusesEnum.TO_DO:
			return { current: 1, isFinalStep: false };
		case AdminOrderStatusesEnum.AWAITING_CONFIRMATION:
			return { current: 2, isFinalStep: false };
		case AdminOrderStatusesEnum.TRANSPORTER_CONFIRMED:
		case AdminOrderStatusesEnum.TRUCK_CONFIRMED:
		case AdminOrderStatusesEnum.IN_PROGRESS:
		case AdminOrderStatusesEnum.TO_LOADING:
		case AdminOrderStatusesEnum.TO_UNLOADING:
			return { current: 3, isFinalStep: false };
		default:
			return { current: 1, isFinalStep: false };
	}
};

const getAdminProgressSimpleExcluded = (
	order: OrderInterface,
): { current: number; isFinalStep: boolean } => {
	const status = order.status as
		| AdminOrderStatusesEnum
		| OrderStatusesEnum
		| undefined;

	const totalSteps = ADMIN_STEPS_SIMPLE_EXCLUDED.length;
	const penultimateStep = Math.max(1, totalSteps - 1);

	if (status === OrderStatusesEnum.PAID) {
		return { current: totalSteps, isFinalStep: true };
	}

	switch (status) {
		case AdminOrderStatusesEnum.TO_DO:
			return { current: 1, isFinalStep: false };
		case AdminOrderStatusesEnum.AWAITING_CONFIRMATION:
			return { current: 2, isFinalStep: false };
		case AdminOrderStatusesEnum.IN_PROGRESS:
			return { current: 3, isFinalStep: false };
		case AdminOrderStatusesEnum.TO_LOADING:
		case AdminOrderStatusesEnum.TRUCK_CONFIRMED:
			return { current: 4, isFinalStep: false };
		case AdminOrderStatusesEnum.DELIVERED:
			return { current: penultimateStep, isFinalStep: false };
		default:
			return { current: 1, isFinalStep: false };
	}
};

const getAdminProgressMultipleExcluded = (
	order: OrderInterface,
): { current: number; isFinalStep: boolean } => {
	const status = order.status as
		| AdminOrderStatusesEnum
		| OrderStatusesEnum
		| undefined;

	const totalSteps = ADMIN_STEPS_MULTIPLE_EXCLUDED.length;
	const penultimateStep = Math.max(1, totalSteps - 1);

	const trucks = Array.isArray(order.trucks) ? order.trucks : [];

	const hasProducerLoadDetails =
		trucks.length > 0 &&
		trucks.every((obj) => obj && 'producerLoadDetails' in obj);
	const isPaid = status === OrderStatusesEnum.PAID;

	if (hasProducerLoadDetails) {
		if (isPaid) {
			return { current: totalSteps, isFinalStep: true };
		}
		return { current: penultimateStep, isFinalStep: false };
	}

	switch (status) {
		case AdminOrderStatusesEnum.TO_DO:
			return { current: 1, isFinalStep: false };
		case AdminOrderStatusesEnum.AWAITING_CONFIRMATION:
			return { current: 2, isFinalStep: false };
		case AdminOrderStatusesEnum.IN_PROGRESS:
		case AdminOrderStatusesEnum.TO_LOADING:
		case AdminOrderStatusesEnum.DELIVERED:
			return { current: 3, isFinalStep: false };
		default:
			return { current: 1, isFinalStep: false };
	}
};

export const getAdminProgress = (order: OrderInterface) => {
	const deliveryIncluded = !!order?.deliveryIncluded;
	const multipleOrder = (order?.trucks?.length || 0) > 1;

	if (deliveryIncluded) {
		if (multipleOrder) {
			return getAdminProgressMultipleIncluded(order);
		}
		return getAdminProgressSimpleIncluded(order);
	}

	if (multipleOrder) {
		return getAdminProgressMultipleExcluded(order);
	}

	return getAdminProgressSimpleExcluded(order);
};
